import { useContext, useRef } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  createStyles,
  makeStyles,
  Theme,
} from '@material-ui/core';
import Markdown from 'react-markdown';
import { BiRefresh } from 'react-icons/bi';
import { FaArrowUp } from 'react-icons/fa6';

import { MAIN_CONTAINER_ID } from '@vyce/core/src/constants';
import { DeviceContext } from '@vyce/core/src/contexts';
import { AppTextField } from '@vyce/core/src/components/inputs';
import { AppIconButton } from '@vyce/core/src/components/AppIconButton';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';

interface Props {
  open: boolean;
  onClose: () => void;
  resultText: string;
  progress: number;
  setProgress: (progress: number) => void;
  loading: boolean;
  regenerateAIAnalytics: () => void;
  loadingText: string;
  focusOfAnalysis: string;
  onFocusOfAnalysisChange: (value: string) => void;
}
export const AIAnalyticsDialog: React.FC<Props> = ({
  open,
  onClose,
  resultText,
  progress,
  setProgress,
  loading,
  regenerateAIAnalytics,
  loadingText,
  focusOfAnalysis,
  onFocusOfAnalysisChange,
}) => {
  const classes = useStyles();
  const { isMobile } = useContext(DeviceContext);
  const { showNotification } = useContext(NotificationContext);
  const markdownRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    onClose();
    setProgress(0);
    onFocusOfAnalysisChange('');
  };

  const copyToClipboard = async () => {
    if (!resultText || !markdownRef.current) {
      return;
    }
    const htmlContent = markdownRef.current.innerHTML;
    const textContent = markdownRef.current.innerText || markdownRef.current.textContent || '';

    const clipboardItem = new ClipboardItem({
      'text/html': new Blob([htmlContent], { type: 'text/html' }),
      'text/plain': new Blob([textContent], { type: 'text/plain' }),
    });

    try {
      await navigator.clipboard.write([clipboardItem]);
    } catch (err) {
      await navigator.clipboard.writeText(textContent);
    }

    showNotification({
      message: 'AI analysis copied to clipboard',
      options: {
        variant: 'success',
      },
    });
  };

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      maxWidth="md"
      fullScreen={isMobile}
      open={open}
      onClose={handleClose}
    >
      <DialogTitle>Vyce AI Analysis</DialogTitle>

      <DialogContent style={{ minWidth: isMobile ? '100%' : 700 }}>
        <Box width="100%" marginBottom={2} marginTop={2}>
          <LinearProgress
            variant="determinate"
            value={progress}
            classes={{
              root: classes.progress,
              bar: classes.bar,
            }}
          />
        </Box>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={isMobile ? '100%' : 640}>
            <Typography color="textSecondary" variant="body1">
              {loadingText}
            </Typography>
          </Box>
        ) : (
          <Box style={{ maxHeight: 640, overflowY: 'auto', paddingBottom: 24 }}>
            <div ref={markdownRef}>
              <Markdown linkTarget="_blank">{resultText}</Markdown>
            </div>
          </Box>
        )}

        <Box display="flex" alignItems="center" gridGap={16} marginTop={2}>
          <AppTextField
            label="Focus of the analysis (optional)"
            fullWidth
            value={focusOfAnalysis}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                regenerateAIAnalytics();
              }
            }}
            onChange={e => onFocusOfAnalysisChange(e.target.value)}
          />

          <AppIconButton disabled={loading} isLarge onClick={regenerateAIAnalytics} variant="primary">
            {<FaArrowUp size="18px" />}
          </AppIconButton>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box display="flex" justifyContent="space-between" width="100%">
          <Button size="large" variant="outlined" onClick={handleClose}>
            Cancel
          </Button>

          <Box display="flex" gridGap={16}>
            <Button
              size="large"
              variant="outlined"
              disabled={loading}
              startIcon={<BiRefresh size="28px" />}
              onClick={regenerateAIAnalytics}
            >
              Regenerate
            </Button>

            <Button
              style={{ width: 180 }}
              size="large"
              onClick={copyToClipboard}
              color="primary"
              disabled={loading || !resultText}
              variant="contained"
            >
              Copy to clipboard
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    progress: {
      width: '100%',
      height: 12,
      borderRadius: 8,
      backgroundColor: theme.palette.background.default,
    },
    bar: {
      backgroundImage: `linear-gradient(270deg, ${theme.palette.primary.main} 100%)`,
    },
  })
);
